import { useWalletHomeAnalytics } from "@shared/analytics/useWalletHomeAnalytics";
import { useWallet } from "@shared/wallet/hooks/useWallet";
import { BuyWidget } from "@solomon-apps/shared";
import React from "react";
import { useSearchParams } from "react-router-dom";

import { AppFootnote } from "@/components/AppFootnote";

import { HeroSection } from "./components/HeroSection";
import { TokenSelector } from "./components/TokenSelector";
import { USDvLandingPanel } from "./components/USDvLandingPanel";

/**
 * @version 1.0.0
 * @published 2026-04-08
 * @history
 *   1.0.0 — Initial publish from prototype/pages/USDv/USDvPage.tsx. Landing
 *           page for the USDv product: navigation bar, a 60/40 split with a
 *           rotating guilloche pattern + hero on the left and the buy widget
 *           on the right, and a footnote anchored to the bottom of the
 *           guilloche panel.
 */
export function USDvPage(): React.ReactElement {
    // `?state=connected-stables-no-usdv` swaps the hero from
    // "Get USDv and earn …" to "Swap to USDv and earn …" — the
    // wallet has USDC/USDT but no USDv, so the natural next action
    // is a swap, not a fresh buy. Other landing-style overrides
    // (`disconnected`, `connected-no-usdv`) render the default
    // headline. Wallet-driven dispatch hasn't been wired to this
    // sub-state yet — only the URL flag flips it for now.
    const [searchParams] = useSearchParams();
    const { connected } = useWallet();
    useWalletHomeAnalytics({
        view: "landing",
        wallet_connected: connected,
        landing_variant: "swap_no_stables"
    });
    const heroMode =
        searchParams.get("state") === "connected-stables-no-usdv"
            ? "swap-to-usdv"
            : "default";
    // Mock USDC + USDT total for the design preview of the swap-to-
    // USDv state. Raw dollar amount — `HeroSection` floors it to a
    // whole dollar and formats with thousands separators for the
    // headline. Will be replaced once wallet-driven dispatch detects
    // the stables balance directly (sum of the wallet's USDC + USDT
    // balances from `useWalletStore` / wallet adapter).
    //   TODO(yaas-into-usdv): wire to live USDC + USDT balance.
    const stablesBalance = heroMode === "swap-to-usdv" ? 1234.56 : undefined;

    return (
        <>
            {/* Body — fills remaining viewport height, 60/40 split on
                desktop, stacked on mobile. No mobile-only collapse
                animation; iOS Safari's auto-scroll-into-view handles
                positioning the input above the keyboard, and the
                sticky header from Layout.tsx stays at the top. */}
            <main className="flex-1 flex flex-col md:flex-row bg-surface-ground overflow-hidden">
                {/* Left desktop panel — shared with the connected-no-yaas
                    and active-position surfaces so all three render the
                    literal same hero chrome. We construct the hero
                    here so this page's `?state=`-driven mode can
                    flow through; the panel itself is mode-agnostic. */}
                <USDvLandingPanel
                    hero={
                        <HeroSection
                            mode={heroMode}
                            stablesBalance={stablesBalance}
                        />
                    }
                />

                {/* Mobile hero — natural height (no `flex-1`). With the
                    shorter `text-3xl` mobile headline (often one line)
                    a flex-1 hero would leave a large gap between the
                    title and the BuyWidget; natural height puts BW
                    directly below the hero with no empty inner space.
                    Empty viewport space accumulates at the bottom —
                    invisible behind the keyboard when an input is
                    focused. Side benefit: no `flex-1` means no
                    cascade from URL-bar collapse, so the freeze hack
                    is no longer needed either. */}
                <div className="flex md:hidden flex-col items-center">
                    <HeroSection
                        mode={heroMode}
                        stablesBalance={stablesBalance}
                    />
                </div>

                {/* Right container — full width on mobile, 40% on desktop.
                    Inner `max-w-md` cap keeps the BuyWidget at its
                    design width inside the wider column (WidgetShell
                    no longer self-constrains; the column owns it). */}
                <div className="md:flex-none md:w-[40%] md:border-l border-line-base flex flex-col items-center justify-start md:justify-center relative px-4 py-5 md:px-0 md:py-0">
                    <div className="w-full max-w-md">
                        {/* No transaction-history entry point here.
                            `USDvHomeRoute` only routes to this page when the
                            wallet holds no USDv and has no YaaS account, so a
                            connected visitor has no account history to open —
                            the link offered a view that was always empty. */}
                        <BuyWidget TokenSelectorComponent={TokenSelector} />
                    </div>
                </div>

                {/* Mobile footnote — static layout, no fade. The
                    keyboard overlays the bottom area when an input is
                    focused, so the footnote is naturally hidden. */}
                <AppFootnote className="md:hidden px-6 pt-4 pb-3" />
            </main>
        </>
    );
}
